// Deterministic bio, headline and mission-statement drafts for
// /tools/freelancer-bio-generator. Runs in the browser with no network calls:
// the same inputs always give the same drafts, and "New versions" moves to the
// next seed. Output is plain text (rendered with text interpolation, never v-html).

const clean = (value, max = 80) => String(value ?? '')
  .replace(/[<>{}\[\]\\]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .slice(0, max);

// "helping startups" / "startups" / "Startups." -> "startups"
const audienceOf = (value) => clean(value)
  .replace(/^(i\s+)?(help|helping|work with|working with|for)\s+/i, '')
  .replace(/[.!]+$/, '');

// "get more leads" / "more leads." -> "get more leads"
const outcomeOf = (value) => clean(value, 100).replace(/^(to\s+|so they can\s+)/i, '').replace(/[.!]+$/, '');

// Lowercases a leading Title-case word ("Brand designer" -> "brand designer") but keeps
// acronyms, mixed case and "I" as typed ("SaaS", "UX", "I teach").
const lowerFirst = (text) => (/^[A-Z][a-z]+\b/.test(text || '') && !/^[A-Z][a-z]*[A-Z]/.test(text) ? text.charAt(0).toLowerCase() + text.slice(1) : text);
const upperFirst = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);
const article = (phrase) => {
  const word = String(phrase).split(/\s/)[0];
  // Acronyms go by letter sound: "an SEO consultant", "a UX writer".
  if (/^[A-Z]{2,}$/.test(word)) return /^[AEFHILMNORSX]/.test(word) ? 'an' : 'a';
  return /^[aeiou]/i.test(word) && !/^(uni|use|eu)/i.test(word) ? 'an' : 'a';
};
const sentence = (text) => {
  const t = upperFirst(String(text).replace(/\s+/g, ' ').trim());
  return t && !/[.!?]$/.test(t) ? `${t}.` : t;
};

function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hash(text) {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  return h >>> 0;
}

// Fisher-Yates with the seeded generator, so variants rotate per seed.
const shuffle = (list, r) => {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(r() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const TONES = {
  professional: { verb: 'helps', verb1: 'help', close: 'The focus is clear communication, steady timelines and useful work.' },
  friendly: { verb: 'helps', verb1: 'help', close: 'The aim is quick replies, clear timelines and plain language.' },
  bold: { verb: 'helps', verb1: 'help', close: 'Straight answers, real deadlines and work you will want to show off.' },
};

export function normalizeBioInput(input = {}) {
  const name = clean(input.name, 60);
  const role = lowerFirst(clean(input.role, 60).replace(/[.!]+$/, ''));
  const audience = audienceOf(input.audience);
  const outcome = outcomeOf(input.outcome);
  const years = Math.max(0, Math.min(60, Math.floor(Number(input.years) || 0)));
  const proof = clean(input.proof, 140).replace(/[.!]+$/, '');
  const personal = clean(input.personal, 120).replace(/[.!]+$/, '');
  const location = clean(input.location, 60);
  const tone = TONES[input.tone] ? input.tone : 'professional';
  const person = input.person === 'third' ? 'third' : 'first';
  return { name, role, audience, outcome, years, proof, personal, location, tone, person };
}

export const hasEnoughForBio = (input) => {
  const d = normalizeBioInput(input);
  return Boolean(d.role && (d.audience || d.outcome));
};

export function generateBios(input = {}, seed = 0) {
  const d = normalizeBioInput(input);
  if (!d.role || !(d.audience || d.outcome)) return null;
  const r = rng(hash(JSON.stringify(d)) + seed * 7919);
  const tone = TONES[d.tone];
  const first = d.person === 'first';
  const firstName = d.name.split(' ')[0] || '';
  const subject = first ? 'I' : (d.name || 'This freelancer');
  const pronounSubject = first ? 'I' : (firstName || 'They');
  const verb = first ? tone.verb1 : tone.verb;
  const roleWithArticle = `${article(d.role)} ${d.role}`;
  const whoServed = d.audience || 'clients';
  const goal = d.outcome ? ` ${d.outcome}` : '';
  // "a designer who helps …": the relative clause is third person in both voices.
  const helpLine = `${tone.verb} ${whoServed}${goal}`;
  const experience = d.years >= 2 ? `${d.years} years` : '';
  const where = d.location ? ` based in ${d.location}` : '';

  // Openers: who + what + for whom.
  const openers = first
    ? [
        `I'm ${d.name ? `${d.name}, ` : ''}${roleWithArticle}${where} who ${helpLine}`,
        `As ${roleWithArticle}${where}, I ${tone.verb1} ${whoServed}${goal}`,
        `I ${tone.verb1} ${whoServed}${goal}. I'm ${d.name ? `${d.name}, ` : ''}${roleWithArticle}${where}`,
      ]
    : [
        `${subject} is ${roleWithArticle}${where} who ${helpLine}`,
        `${subject}, ${roleWithArticle}${where}, ${helpLine}`,
        `${subject} ${verb} ${whoServed}${goal} as ${roleWithArticle}${where}`,
      ];
  const [opener, altOpener] = shuffle(openers, r);

  const expLine = experience
    ? sentence(first
      ? pick(r, [`I've spent ${experience} doing this work`, `${upperFirst(experience)} in, I know what makes a project run smoothly`])
      : pick(r, [`${pronounSubject} has ${experience} of experience`, `${pronounSubject} brings ${experience} of experience to every project`]))
    : '';
  const proofLine = d.proof ? sentence(first ? `Recent work: ${lowerFirst(d.proof)}` : `Recent work includes ${lowerFirst(d.proof)}`) : '';
  const personalLine = d.personal ? sentence(first ? `Outside of work, ${/^I\b/.test(d.personal) ? d.personal : lowerFirst(d.personal)}` : `Outside of work, ${firstName || 'they'} ${lowerFirst(d.personal).replace(/^i\s+/i, '')}`) : '';
  const closeLine = sentence(tone.close.replace('{who}', first ? 'me' : (firstName || 'them')));
  const ctaLine = first
    ? pick(r, ['If that sounds like what you need, get in touch.', "Send me a note and tell me what you're working on.", "Let's talk about your next project."])
    : '';

  const short = [sentence(opener), proofLine || expLine].filter(Boolean).join(' ');
  const medium = [sentence(opener), expLine, proofLine, closeLine].filter(Boolean).join(' ');
  const long = [sentence(opener), expLine, proofLine, closeLine, personalLine, ctaLine].filter(Boolean).join(' ');

  const roleTitle = d.role.replace(/\b\w/g, (c) => c.toUpperCase());
  const audienceTitle = whoServed.replace(/^\w/, (c) => c.toUpperCase());
  const headlines = shuffle([
    `${roleTitle} for ${whoServed}${d.outcome ? ` | I help you ${d.outcome}` : ''}`,
    `${roleTitle} | Helping ${whoServed}${goal}`,
    `I help ${whoServed}${goal} | ${roleTitle}`,
    `${roleTitle}${experience ? ` · ${experience}` : ''}${d.audience ? ` · ${audienceTitle}` : ''}`,
    d.proof ? `${roleTitle} | ${upperFirst(d.proof)}` : `${roleTitle} | Clear, on-time work for ${whoServed}`,
  ], r).map((h) => h.slice(0, 220));

  const mission = shuffle([
    `To ${d.outcome ? `help ${whoServed}${goal}` : `give ${whoServed} work they can rely on`}, with honest communication and work done right the first time.`,
    `Every client should finish a project better off than they started${d.outcome ? `: for ${whoServed}, that means being able to${goal}` : ''}.`,
    `We exist to ${d.outcome ? `help ${whoServed}${goal}` : `make ${whoServed}'s lives easier`}, one well-run project at a time.`,
  ], r);

  return {
    short,
    medium,
    long,
    alternative: sentence(altOpener),
    headlines: [...new Set(headlines)].slice(0, 4),
    mission: mission.slice(0, 2),
  };
}

function pick(r, list) {
  return list[Math.floor(r() * list.length)];
}

export const wordCount = (text) => (String(text).trim() ? String(text).trim().split(/\s+/).length : 0);
